import { Input } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { SendlaneIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSendlaneCRUDActions } from '@mm/state/actions/SendlaneCRUDActions';
import ConnectionModel, {
  ConnectionKind,
} from '@mm/state/model/ConnectionModel';
import { selectSendlaneConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Spin } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { HelpCenter } from '../../../../../../../../common/components/HelpCenter/HelpCenter';
import { BaseIntegrationEmptyState } from '../../../BaseIntegration/BaseIntegrationEmptyState';
import { OauthTokenStatus } from '../OauthTokenStatus/OauthTokenStatus';
import styles from './SendlaneIntegrations.module.css';
import { SendlaneSyncSetup } from './SendlaneSyncSetup';

export const SendlaneSettings = ({
  connection,
  version,
}: {
  connection?: ConnectionModel;
  version?: 'widget';
}) => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const apiKeys = useSelector((store: StoreState) => store.sendlane.apiKeys);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const isWidgetMode = version === 'widget';
  const sendlaneConnections = useSelector(selectSendlaneConnections).filter(
    c => c.kind === ConnectionKind.AUDIENCE,
  );
  const sendlaneConnection = isWidgetMode
    ? connection
    : sendlaneConnections?.[0];

  const accessToken =
    apiKeys.find(
      apiKey => apiKey.id === sendlaneConnection?.sendlaneAccessToken?.id,
    ) || apiKeys[0];

  const [busy, setBusy] = useState(false);
  const [apiKey, setApiKey] = useState(accessToken?.privateKey);
  const [error, setError] = useState('');

  const disabledApiKey =
    sendlaneConnection && apiKey === accessToken?.privateKey;
  const helpLink =
    'https://customers.ai/help/l/en/article/jb7zslbl8x-sendlane-integration';

  const { getLists, getApiKeys, createApiKey, updateApiKey } =
    useSendlaneCRUDActions();
  const { update, create } = useConnectionCRUDActions();

  const { openNewBillingModal2023 } = useModalActions();
  const notMyToken = accessToken?.readOnly;

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.SENDLANE_UPGRADE,
      onSuccessBilling,
    });
  };

  const createConnection = async (id: number) => [
    await create(activeFbPageId, {
      active: false,
      name: 'Sendlane Connection',
      connectionKind: 'sendlane',
      sendlaneParams: { accessTokenId: id },
    }),
  ];

  const connectApiKey = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (!apiKey || apiKey.length < 900) {
      setError('Invalid API Key');
      return;
    }

    const createKey = async (privateKey: string) => {
      const { id } = await createApiKey(privateKey);

      if (!sendlaneConnection && !isWidgetMode) {
        createConnection(id);
      } else if (
        (sendlaneConnections || []).find(
          conn => conn.sendlaneAccessToken?.id !== id,
        )
      ) {
        sendlaneConnections.forEach(connection => {
          update(
            activeFbPageId,
            {
              id: connection.id,
              sendlaneParams: { accessTokenId: id },
            },
            'sendlane',
          );
        });
      }
    };

    if (!hasNativeIntegrationsFeature) {
      showUpgradeModal(connectApiKey);
    } else {
      if (accessToken) {
        updateApiKey(accessToken.id, apiKey);
      } else {
        createKey(apiKey);
      }
      retrieveSendlaneLists();
      setError('');
    }
  };

  const retrieveSendlaneLists = async () => {
    if (accessToken?.id && hasNativeIntegrationsFeature) {
      await getLists(activeFbPageId, accessToken.id);
    }
  };

  useEffect(() => {
    (async () => {
      setBusy(true);
      await getApiKeys(activeFbPageId);
      retrieveSendlaneLists();
      setBusy(false);
    })();
  }, []);

  useEffect(() => {
    setApiKey(accessToken?.privateKey);
  }, [accessToken]);

  if (isWidgetMode && busy) {
    return <Spin />;
  }

  return (
    <div className={styles.settingsContent}>
      <div>
        <span className={styles.label}>Credentials</span>
        <div
          className={classNames(styles.box, styles.boxWithHelp, {
            [styles.widgetIntgration]: isWidgetMode,
          })}
        >
          {((isWidgetMode && !accessToken) || !isWidgetMode) && (
            <div className={styles.boxContent}>
              <div className={styles.heading}>API Key</div>
              <div className={styles.apiKeyInput}>
                <Input
                  placeholder="Enter your API Key"
                  css={{
                    container: styles.inputContainer,
                    input: styles.input,
                  }}
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                />
                <PrimaryButton
                  className={styles.primaryButton}
                  onClick={e => connectApiKey(e)}
                  disabled={disabledApiKey}
                >
                  {accessToken && disabledApiKey
                    ? 'Connected'
                    : accessToken && accessToken?.privateKey !== apiKey
                    ? 'Update'
                    : 'Connect'}
                </PrimaryButton>
              </div>
              <div className={styles.error}>{error}</div>
            </div>
          )}
          {isWidgetMode && accessToken && (
            <div className={classNames(styles.status)}>
              <div className={styles.title}>Account</div>
              <div className={styles.statusRow}>
                <OauthTokenStatus
                  token={accessToken}
                  styles={{ oauthStatus: styles.oauthStatus }}
                  connectedTooltipMsg={`API Key: ${accessToken.privateKey}`}
                />
                <HelpCenter helpLink={helpLink} className={styles.help} />
              </div>
            </div>
          )}
        </div>
      </div>
      {!sendlaneConnection && (
        <BaseIntegrationEmptyState
          icon={
            <div
              className={classNames(
                'integrations__header-icon',
                'integrations__sendlane-header-icon',
              )}
              style={{ margin: 0 }}
            >
              <SendlaneIcon />
            </div>
          }
          button="Add a Connection"
          landmineName={landmineNames.SENDLANE_UPGRADE}
          onClick={() => createConnection(accessToken.id)}
        />
      )}

      {accessToken && !isWidgetMode && sendlaneConnection && (
        <div>
          <span className={styles.label}>Destinations</span>

          <SendlaneSyncSetup
            accessToken={accessToken}
            controlsDisabled={!!notMyToken}
          />
        </div>
      )}
    </div>
  );
};
